import { useState } from "react";
import * as Icons from "lucide-react";
import { Download, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RESOURCES } from "@/data/content";
import { Seo } from "@/components/Seo";

export default function Resources() {
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const openFor = (r) => { setActive(r); setForm({ name: "", email: "" }); setSent(false); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/resource", {
        name: form.name, email: form.email, source: "resource",
        message: `Requested: ${active.title}`, meta: { resource: active.slug },
      });
      setSent(true);
      toast.success("Sent! Check your inbox shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Seo
        title="Free Insurance Guides & Checklists NZ | Quantum Cover"
        description="Download free insurance guides and checklists to help you understand your cover and take the first step with confidence."
        path="/resources"
      />
      <section className="bg-[#0B3D2E] text-white pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400 mb-3">Free Resources</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Free guides to get you started</h1>
          <p className="mt-5 text-lg text-slate-300">A few handy checklists and guides to help you work out your next move. Grab whichever ones are useful.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {RESOURCES.map((r) => {
            const Icon = Icons[r.icon] || Icons.FileText;
            return (
              <div key={r.slug} data-testid={`resource-card-${r.slug}`} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex gap-5">
                <span className="grid place-items-center h-14 w-14 shrink-0 rounded-2xl bg-emerald-50 text-emerald-600"><Icon size={26} /></span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-xl font-semibold text-[#0B3D2E]">{r.title}</h3>
                  <p className="mt-1.5 text-slate-600 leading-relaxed">{r.desc}</p>
                  <Button onClick={() => openFor(r)} data-testid={`resource-download-${r.slug}`} className="mt-5 rounded-full bg-[#0B3D2E] hover:bg-[#124A35] text-white px-5"><Download size={16} className="mr-2" /> Download free</Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#0B3D2E]">{sent ? "You're all set!" : active?.title}</DialogTitle>
          </DialogHeader>
          {sent ? (
            <div className="text-center py-4" data-testid="resource-success">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto"><Check size={28} /></span>
              <p className="mt-4 text-slate-600">We've sent <strong>{active?.title}</strong> to <strong>{form.email}</strong>. Enjoy!</p>
              <Button onClick={() => setActive(null)} className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6">Close</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-sm text-slate-600">Enter your details and we'll email your free copy.</p>
              <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resource-name" className="rounded-xl h-12" />
              <Input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="resource-email" className="rounded-xl h-12" />
              <Button type="submit" disabled={loading} data-testid="resource-submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6">{loading ? "Sending…" : "Send me the guide"}</Button>
              <p className="text-xs text-slate-400 text-center">We'll never spam you or share your details.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
